import {useState} from 'react';
import {thumbsUp, thumbsDown} from '@sail/icons';
import {tokens, Action, Icon, view, useTokenValue} from '@sail/ui';

/**
 * This is for the feedback v2 with thumbs up/down non-blocking feedback project.
 * @see https://home.corp.stripe.com/compass/projects/thumbs-up-thumbs-down-on-universal-help
 */
export const FeedbackButtons = ({
  onThumbsUp,
  onThumbsDown,
  value,
}: {
  onThumbsUp: () => Promise<void>;
  onThumbsDown: () => Promise<void>;
  value: 'thumbs_up' | 'thumbs_down' | 'none';
}) => {
  const [isPending, setIsPending] = useState(false);
  const space150 = useTokenValue(tokens.space[150]);
  const alreadyThumbsUp = value === 'thumbs_up';
  const alreadyThumbsDown = value === 'thumbs_down';
  return (
    <view.div
      css={{
        cursor: isPending ? 'wait' : 'pointer',
        position: 'absolute',
        bottom: `-${space150}`,
        right: tokens.space[150],
        display: 'flex',
        gap: tokens.space.xxsmall,
        alignItems: 'center',
        backgroundColor: tokens.color.neutral[0],
        borderRadius: tokens.border.radius.rounded,
        padding: `0 ${tokens.space.small}`,
        boxShadow: tokens.shadow.medium,
        opacity: 0,
        transition: 'opacity 0.2s ease-in-out',
        '.bot-agent-hover-group:hover &': {
          opacity: 1,
        },
      }}
    >
      <Action
        disabled={isPending}
        aria-label="Thumbs up"
        onPress={() => {
          setIsPending(true);
          onThumbsUp().finally(() => setIsPending(false));
        }}
        css={{
          paddingRight: tokens.space.xsmall,
          paddingLeft: tokens.space.xxsmall,
          paddingTop: tokens.space.xxsmall,
          paddingBottom: tokens.space.xxsmall,
          cursor: 'inherit',
        }}
      >
        <Icon
          icon={thumbsUp}
          size="xsmall"
          css={{
            opacity: isPending ? 0.8 : 1,
            fill: alreadyThumbsUp
              ? tokens.color.icon.action.primary
              : tokens.color.icon.action.secondary,
          }}
        />
      </Action>
      <Action
        disabled={isPending}
        aria-label="Thumbs down"
        onPress={() => {
          setIsPending(true);
          onThumbsDown().finally(() => setIsPending(false));
        }}
        css={{
          paddingRight: tokens.space.xxsmall,
          paddingLeft: tokens.space.xsmall,
          paddingTop: tokens.space.xxsmall,
          paddingBottom: tokens.space.xxsmall,
          cursor: 'inherit',
        }}
      >
        <Icon
          icon={thumbsDown}
          size="xsmall"
          css={{
            opacity: isPending ? 0.8 : 1,
            fill: alreadyThumbsDown
              ? tokens.color.icon.action.primary
              : tokens.color.icon.action.secondary,
          }}
        />
      </Action>
    </view.div>
  );
};
